import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  styled
} from '@mui/material';
import { 
  ArrowBackIos as ArrowBackIosIcon, 
  ArrowForwardIos as ArrowForwardIosIcon 
} from '@mui/icons-material';
import { format, parseISO, addMonths, startOfMonth, getDate, getHours } from 'date-fns';

interface AvailableTime {
  iso: string;
  date: string;
  time: string;
  day_of_week: string;
  formatted: string;
}

interface TimeSelectionProps {
  availableTimes: AvailableTime[];
  selectedTime: AvailableTime | null;
  onTimeSelect: (time: AvailableTime) => void;
}

// Simple calendar day component with indicator
const CalendarDay = styled('div')(({ theme }) => ({
  width: '36px',
  height: '36px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  borderRadius: '50%',
  margin: '2px',
  fontSize: '0.875rem',
  cursor: 'pointer',
  '&.outside-month': {
    color: theme.palette.text.disabled,
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '&.today': {
    fontWeight: 'bold',
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '&.has-times': {
    fontWeight: 'bold',
  },
  '&:hover:not(.disabled)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.disabled': {
    color: theme.palette.text.disabled,
    cursor: 'default',
  }
}));

// Indicator dots for morning/afternoon availability
const AvailabilityIndicator = styled('div')(({ theme, period }) => ({
  position: 'absolute',
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: period === 'morning' ? theme.palette.primary.main : theme.palette.secondary.main,
  bottom: '2px',
  left: period === 'morning' ? 'calc(50% - 8px)' : 'calc(50% + 2px)',
}));

const TimeSelection: React.FC<TimeSelectionProps> = ({ 
  availableTimes, 
  selectedTime, 
  onTimeSelect 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Group times by date
  const timesByDate: Record<string, AvailableTime[]> = {};
  const availableDateStrings: string[] = [];
  
  availableTimes.forEach(time => {
    const timeDate = time.date;
    if (!timesByDate[timeDate]) {
      timesByDate[timeDate] = [];
      availableDateStrings.push(timeDate);
    }
    timesByDate[timeDate].push(time);
  });
  
  // Current month view (default to current month)
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (availableDateStrings.length > 0) {
      // Use the first available date's month
      return startOfMonth(parseISO(availableDateStrings[0]));
    }
    return startOfMonth(new Date());
  });
  
  // For the selected date's available times
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Set selectedDate initially or when availableTimes changes
  useEffect(() => {
    if (availableDateStrings.length > 0 && !selectedDate) {
      // Sort dates and select the earliest one
      const sortedDates = [...availableDateStrings].sort();
      setSelectedDate(sortedDates[0]);
    }
  }, [availableTimes, selectedDate, availableDateStrings]);

  // Check if a date has morning times (before noon)
  const hasMorningTimes = (dateStr: string) => {
    if (!timesByDate[dateStr]) return false;
    return timesByDate[dateStr].some(time => {
      const hour = getHours(new Date(time.iso));
      return hour < 12;
    });
  };

  // Check if a date has afternoon times (noon or after)
  const hasAfternoonTimes = (dateStr: string) => {
    if (!timesByDate[dateStr]) return false;
    return timesByDate[dateStr].some(time => {
      const hour = getHours(new Date(time.iso));
      return hour >= 12;
    });
  };

  // Generate calendar days for the current month
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get days in the current month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Get the first day of the month (0-6, Sunday-Saturday)
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    // Calculate days from previous month to display
    const daysFromPrevMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Days array to hold all calendar days
    const days: { date: Date; outsideMonth: boolean }[] = [];
    
    // Add days from previous month
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    
    for (let i = prevMonthDays - daysFromPrevMonth + 1; i <= prevMonthDays; i++) {
      days.push({
        date: new Date(prevMonth.getFullYear(), prevMonth.getMonth(), i),
        outsideMonth: true
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        outsideMonth: false
      });
    }
    
    // Calculate remaining days to add from next month (to complete the grid)
    const totalDays = days.length;
    const remainingDays = 42 - totalDays; // 6 rows of 7 days
    
    // Add days from next month
    const nextMonth = new Date(year, month + 1, 1);
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(nextMonth.getFullYear(), nextMonth.getMonth(), i),
        outsideMonth: true
      });
    }
    
    return days;
  };

  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      const prevMonth = new Date(prev);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      return prevMonth;
    });
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      const nextMonth = new Date(prev);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      return nextMonth;
    });
  };

  // Handle date selection
  const handleDateSelect = (day: Date) => {
    const formattedDate = format(day, 'yyyy-MM-dd');
    if (timesByDate[formattedDate]) {
      setSelectedDate(formattedDate);
    }
  };

  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  if (availableTimes.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          No Available Times
        </Typography>
        <Typography color="text.secondary">
          There are no available appointment times for the selected service and practitioner.
          Please try a different service, practitioner, or contact us directly to schedule.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select a Date & Time
      </Typography>

      <Grid container spacing={3}>
        {/* Calendar section */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={handlePrevMonth} size="small">
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6">
                {format(currentMonth, 'MMMM yyyy')}
              </Typography>
              <IconButton onClick={handleNextMonth} size="small">
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Weekday headers */}
            <Grid container justifyContent="center">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <Grid item key={day} xs={1.7}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar grid */}
            <Grid container justifyContent="center">
              {getDaysInMonth().map((dayObj, index) => {
                const day = dayObj.date;
                const dateStr = format(day, 'yyyy-MM-dd');
                const hasTimes = !!timesByDate[dateStr];
                const hasMorning = hasMorningTimes(dateStr);
                const hasAfternoon = hasAfternoonTimes(dateStr);
                const isSelected = selectedDate === dateStr;
                
                return (
                  <Grid item key={index} xs={1.7}>
                    <Box display="flex" justifyContent="center">
                      <CalendarDay
                        className={`
                          ${dayObj.outsideMonth ? 'outside-month' : ''}
                          ${isSelected ? 'selected' : ''}
                          ${isToday(day) ? 'today' : ''}
                          ${hasTimes ? 'has-times' : ''}
                          ${!hasTimes ? 'disabled' : ''}
                        `}
                        onClick={() => hasTimes && handleDateSelect(day)}
                      >
                        {getDate(day)}
                        {hasMorning && <AvailabilityIndicator period="morning" />}
                        {hasAfternoon && <AvailabilityIndicator period="afternoon" />}
                      </CalendarDay>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  width: 10, 
                  height: 10, 
                  bgcolor: theme.palette.primary.main, 
                  borderRadius: '50%', 
                  mr: 0.5 
                }} />
                <Typography variant="caption">Morning</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  width: 10, 
                  height: 10, 
                  bgcolor: theme.palette.secondary.main, 
                  borderRadius: '50%', 
                  mr: 0.5 
                }} />
                <Typography variant="caption">Afternoon</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Time slots section */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            {selectedDate ? (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Available times for {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}:
                </Typography>
                
                {timesByDate[selectedDate] ? (
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    {timesByDate[selectedDate].map((time) => {
                      // Determine if it's morning or afternoon
                      const hour = getHours(new Date(time.iso));
                      const timeOfDay = hour < 12 ? 'morning' : 'afternoon';
                      
                      return (
                        <Grid item xs={6} sm={4} key={time.iso}>
                          <Button
                            variant={selectedTime?.iso === time.iso ? "contained" : "outlined"}
                            color={timeOfDay === 'morning' ? 'primary' : 'secondary'}
                            onClick={() => onTimeSelect(time)}
                            fullWidth
                            sx={{ 
                              height: isMobile ? 'auto' : 60,
                              textTransform: 'none'
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="body2" component="span">
                                {time.time}
                              </Typography>
                            </Box>
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography color="text.secondary">
                    No available times for the selected date.
                  </Typography>
                )}
              </>
            ) : (
              <Typography color="text.secondary">
                Please select a date from the calendar to view available times.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {selectedTime && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={`Selected: ${selectedTime.formatted}`}
            color="primary"
            onDelete={() => onTimeSelect(selectedTime)} // Click again to confirm
          />
        </Box>
      )}
    </Box>
  );
};

export default TimeSelection;